export interface GoogleFont {
  family: string;
  variants: string[];
  subsets: string[];
  version: string;
  lastModified: string;
  files: {
    [key: string]: string;
  };
  category: string;
  kind: string;
  menu?: string;
}

export interface GoogleFontsResponse {
  kind: string;
  items: GoogleFont[];
}

const DEFAULT_FONTS: GoogleFont[] = [];

interface FetchGoogleFontsOptions {
  page?: number;
  pageSize?: number;
}

export const fetchGoogleFonts = async (options: FetchGoogleFontsOptions = {}): Promise<{fonts: GoogleFont[], total: number}> => {
  const { page = 1, pageSize = 50 } = options;
  const apiKey = process.env.REACT_APP_GOOGLE_FONTS_API_KEY;
  
  if (!apiKey) {
    return { fonts: DEFAULT_FONTS, total: DEFAULT_FONTS.length };
  }

  try {
    const response = await fetch(`https://www.googleapis.com/webfonts/v1/webfonts?key=${apiKey}&sort=popularity`);
    if (!response.ok) {
      throw new Error(`Failed to fetch Google Fonts: ${response.statusText}`);
    }
    const data: GoogleFontsResponse = await response.json();
    
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedFonts = (data.items || []).slice(startIndex, endIndex);
    
    return {
      fonts: paginatedFonts,
      total: data.items?.length || 0
    };
  } catch (error) {
    return { fonts: DEFAULT_FONTS, total: DEFAULT_FONTS.length };
  }
};

export const getGoogleFontUrl = (fontFamily: string, variants: string[] = ['400', '500', '600', '700']): string => {
  const family = fontFamily.replace(/\s+/g, '+');
  const variantsParam = variants.join(';');
  return `https://fonts.googleapis.com/css2?family=${family}:wght@${variantsParam}&display=swap`;
};

export const getGoogleFontCss = (fontFamily: string): string => {
  return `font-family: '${fontFamily}', sans-serif;`;
};

export const loadGoogleFont = (fontFamily: string, variants: string[] = ['400', '700']): void => {
  const link = document.createElement('link');
  link.href = getGoogleFontUrl(fontFamily, variants);
  link.rel = 'stylesheet';
  document.head.appendChild(link);
};
